import { useState } from "react";
import { api } from "../api";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface YAnswer {
  text: string;
  pick?: number;
  right?: boolean;
  author?: string;
}

interface View {
  prompt: string;
  yesterdayPrompt: string;
  yesterday: YAnswer[];
  candidates: { id: number; name: string }[];
  answered?: string;
  answeredCount: number;
}

const COPY = {
  bad_match: "Не вийшло, спробуй ще раз",
  already_matched: "Цю відповідь ти вже вгадував",
  bad_answer: "Відповідь: 2-200 символів",
};

export function Dviinyk({ date }: { date?: string | undefined }) {
  const g = useGame<View>("dviinyk", COPY, date);
  const [text, setText] = useState("");
  const [open, setOpen] = useState<number | null>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  const match = async (idx: number, userId: number) => {
    setOpen(null);
    const next = await g.run(() => api.guess<View>("dviinyk", `m:${idx}:${userId}`, date));
    if (next) {
      const extra = next.extra as { right?: boolean } | undefined;
      if (extra?.right) g.showToast("Є збіг! 🎯");
      else {
        haptic.error();
        g.showToast("Повз");
      }
    }
  };

  const answer = async () => {
    const t = text.trim();
    if (t.length < 2 || g.busy) return;
    await g.run(() => api.guess<View>("dviinyk", `a:${t}`, date));
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const matchable = v.yesterday.length > 0 && v.candidates.length >= 2;
  const matchedAll = v.yesterday.every((y) => y.pick !== undefined);
  const right = v.yesterday.filter((y) => y.right).length;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ДВІЙНИК" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-3">
        {matchable && (
          <div className="mt-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-tghint">
              вчорашні відповіді - вгадай, хто що написав {matchedAll ? `· ${right}/${v.yesterday.length} 🎯` : ""}
            </div>
            <div className="mt-1.5 rounded-2xl bg-[rgba(128,128,128,0.12)] px-4 py-2.5">
              <div className="text-[11px] text-tghint">вчорашнє питання</div>
              <div className="mt-0.5 text-[15px] font-semibold leading-snug">{v.yesterdayPrompt}</div>
            </div>
            <ul className="mt-2 space-y-2">
              {v.yesterday.map((y, i) => (
                <li key={i} className="rounded-xl bg-tgsection px-3 py-2.5">
                  <div className="text-[15px] leading-snug">«{y.text}»</div>
                  {y.pick !== undefined ? (
                    <div className={`mt-1.5 text-sm font-semibold ${y.right ? "text-ok" : "text-tghint"}`}>
                      {y.right ? `✓ ${y.author}` : "✗ повз"}
                    </div>
                  ) : finished ? (
                    <div className="mt-1.5 text-xs text-tghint">не вгадував</div>
                  ) : open === i ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {v.candidates.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          disabled={g.busy}
                          onClick={() => void match(i, c.id)}
                          className="rounded-full bg-[rgba(128,128,128,0.18)] px-3 py-1.5 text-sm active:opacity-70"
                        >
                          {c.name}
                        </button>
                      ))}
                      <button type="button" onClick={() => setOpen(null)} className="rounded-full px-3 py-1.5 text-sm text-tghint">
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setOpen(i)}
                      className="mt-1.5 rounded-lg bg-tgbutton/15 px-3 py-1.5 text-sm font-semibold active:opacity-70"
                    >
                      хто це написав?
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <div className="my-3 h-px bg-[rgba(128,128,128,0.2)]" />
          </div>
        )}

        <div className="text-xs font-semibold uppercase tracking-wide text-tghint">питання дня - твоя відповідь піде в завтрашню гру</div>
        <div className="mt-1.5 rounded-2xl bg-tgsection px-4 py-3 text-[15px] font-semibold leading-snug">{v.prompt}</div>

        {v.answered ? (
          <div className="mt-2 rounded-2xl bg-tgbutton/15 px-4 py-3">
            <div className="text-[15px] leading-snug">«{v.answered}»</div>
            <div className="mt-1 text-xs text-tghint">
              прийнято 🤫 {v.answeredCount > 1 ? `вже відповіли: ${v.answeredCount}` : "ти перший"} · завтра чат вгадуватиме
            </div>
          </div>
        ) : (
          <div className="mt-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              maxLength={200}
              placeholder="чесно і коротко - анонімність до завтра гарантована"
              className="w-full rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-4 py-3 text-[16px] text-tgtext outline-none placeholder:text-[rgba(128,128,128,0.6)] focus:border-tgbutton"
            />
            {matchable && !matchedAll && (
              <div className="mt-1 text-center text-[11px] text-tghint">спершу повгадуй вчорашні - після відповіді вони закриються</div>
            )}
            <button
              type="button"
              onClick={() => void answer()}
              disabled={g.busy || text.trim().length < 2}
              className="mt-2 w-full rounded-xl bg-tgbutton py-3 text-sm font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
            >
              {g.busy ? "…" : "відповісти"}
            </button>
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title="Записано!"
          subtitle={
            matchable
              ? `збіги ${right}/${v.yesterday.length} · стрік ${s.me.streak} 🔥`
              : `завтра чат вгадуватиме твою відповідь · стрік ${s.me.streak} 🔥`
          }
          scoreLabel="avg промахів"
          formatScore={(n) => `${n ?? "–"}`}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
